import { checkBounds } from "../../../../dsp/signal/helpers/data-lookup.js";
import { Metronome, metronome } from "../../../../dsp/signal/metronome.js";
import { signal as createSignal, Signal } from "../../../../dsp/signal/signal.js";
import { createHandler } from "./handlers.js";
/**
 * Returns the index of the probe within this data set.
 * N.B. : This setup does not allow to uses a probe twice
 * within a data set. E.G. two temperature probes within
 * a meteorological station, one at a height of 1 metre  and one
 * at 10 metre
 * @param encodedDataSet The data set to get the probe index for.
 * @param probeId The id of the probe
 * @returns The index of the probe within this data set
 */
export const getProbeIndex = (encodedDataSet, probeId) => {
    return encodedDataSet.handlers
        .flatMap((handler) => handler.probes.map((probe) => probe.id))
        .indexOf(probeId);
};
export class DataSet {
    _handlers;
    startMillis;
    endMillis;
    averageInterval;
    encodedData;
    _tupleSize;
    _probeCount;
    _probeToHandler;
    _probeIds;
    //getter for the actual timestamps present in the data
    _timeStamp;
    /**
     * Returns a DataSet based on given definition
     * @param encodedDataSet the definition as provided by the server
     * @returns The DataSet
     */
    static get = (encodedDataSet) => {
        return new DataSet(encodedDataSet);
    };
    constructor(encodedDataSet) {
        this.startMillis = Date.parse(encodedDataSet.start);
        this.endMillis = Date.parse(encodedDataSet.end);
        this.averageInterval = encodedDataSet.interval;
        this._handlers = encodedDataSet.handlers.map((handlerDef) => createHandler(handlerDef));
        this.encodedData = encodedDataSet.data;
        this._timeStamp = (index) => { checkBounds(index, this.encodedData); return this.encodedData[index][0]; };
        //build a mapping from probeIndex to { handlerIndex, handlerProbeIndex}
        this._tupleSize = 0;
        this._probeCount = 0;
        this._probeToHandler = this._handlers.reduce((pth, handler, hIndex) => {
            handler.probes().forEach((_probe, pIndex) => pth.push({
                handler: hIndex,
                probe: pIndex,
                tuple: this._tupleSize + 1,
            }));
            this._tupleSize += handler.encodeSize();
            this._probeCount += handler.probes().length;
            return pth;
        }, []);
        //console.log('_INDEX=',this._probeToHandler);
        this._probeIds = this._handlers.reduce((probes, handler) => {
            handler.probes().forEach((_probe) => probes.push(_probe.id));
            return probes;
        }, []);
        //create non uniform data set with gaps
        // const _timing = this.encodedData.map((datumTuple, i) => {
        //   let t = datumTuple[0] + (Math.random() - 0.5) * this.averageInterval;
        //   if (i >= 10) t += this.averageInterval*2; //gap
        //   if (i >= 15) t += this.averageInterval*2.1; //gap
        //   return t;
        // });
        // //check if we got gaps
        // _timing.forEach((t, i, tList) => {
        //   this.encodedData[i][0] = _timing[i];
        //   if (i > 0) {
        //     if (t - tList[i - 1] >= 2 * this.averageInterval) {
        //       console.log(`gap between ${i - 1} and ${i}`);
        //     }
        //   }
        // });
        // console.log(this.encodedData);
    }
    /**
     * Return the start and end timestamps where this data set is defined for. The
     * data set does not necessarily have data for this complete range. E.g. a sensor
     * was off-line.
     *
     * @returns the start and end timestamps where this data set is defined for.
     */
    timeExtent() {
        return [this.startMillis, this.endMillis];
    }
    /**
     * @returns the number of encoded channels within the dataset;
     */
    tupleSize() {
        return this._tupleSize;
    }
    /**
     * @returns the number od decoded probes within the dataset;
     */
    probeCount() {
        return this._probeCount;
    }
    /**
     * @returns the handlers used within the dataset
     */
    handlers() {
        return this._handlers;
    }
    probeIndex(probeId) {
        let _probeIndex = this._probeIds.indexOf(probeId);
        return _probeIndex !== -1 ? _probeIndex : undefined;
    }
    probe(probeIndex) {
        return this._handlers[this._probeToHandler[probeIndex].handler].probes()[this._probeToHandler[probeIndex].probe];
    }
    /**
     * Returns an Iterable for  raw/encoded data for given tupleIndex.
     * @param tupleIndex the index of the encoded channel.
     * @returns the Iterable for tupleIndex.
     */
    getRaw(tupleIndex) {
        const dataSet = this;
        return {
            *[Symbol.iterator]() {
                let index = 0;
                let data = dataSet.rawData(tupleIndex);
                while (index < dataSet.encodedData.length) {
                    yield data(index++);
                }
            },
        };
    }
    /**
     * Returns an Iterable for probe/decoded data for given probeIndex.
     * @param probeIndex the index of the decoded probe.
     * @returns the Iterable for probeIndex.
     */
    get(probeIndex) {
        const dataSet = this;
        return {
            *[Symbol.iterator]() {
                let index = 0;
                let data = dataSet.data(probeIndex);
                while (index < dataSet.encodedData.length) {
                    yield data(index++);
                }
            },
        };
    }
    /**
     * Returns a signal for given encoded channel
     * @param channelIndex the index of the encoded channel
     * @returns  the signal
     */
    // public rawSignal(channelIndex: number): Signal {
    //   return createSignal(
    //     this._timeStamp,
    //     this.rawData(channelIndex),
    //     this.averageInterval,
    //     undefined,
    //     undefined,
    //     undefined
    //   );
    // }
    /**
     * Returns a signal for given decoded probe
     * @param probeIndex the index of the decoded probe
     * @param targetUnit the unit you want to work with (may be the same as the source unit)
     * @returns the signal
     */
    signal(probeIndex, targetUnit) {
        const sourceUnit = this.probe(probeIndex).isqUnit;
        return createSignal(this._timeStamp, this.data(probeIndex), this.averageInterval, this._handlers[this._probeToHandler[probeIndex].handler].codecs()[this._probeToHandler[probeIndex].probe], sourceUnit, targetUnit ?? sourceUnit);
    }
    /**
     * Returns a metronome that is valid for the time range of this data set.
     * @param intervalInSeconds The interval (in seconds) at which the metronome should run
     * @returns the metronome
     */
    getMetronome(intervalInSeconds) {
        return metronome(this.startMillis, this.endMillis, intervalInSeconds * 1000, true);
    }
    /**
     * Returns the timing (implements the Timing interface) for the underlying
     * data.
     * @returns timing for this dataset
     */
    timing() {
        const dataSet = this;
        let s = this.startMillis;
        let e = this.endMillis;
        let l = 0;
        if (this.encodedData && this.encodedData.length > 0) {
            l = this.encodedData.length;
            s = this.encodedData[0][0];
            e = this.encodedData[l - 1][0];
        }
        return {
            start: () => s,
            end: () => e,
            count: () => l,
            get: (index) => {
                if (index >= 0 && index < l) {
                    return this.encodedData[index][0];
                }
                else {
                    throw new RangeError(`DataSet time index ${index} is not valid. 0 <= index < ${this.encodedData.length}`);
                }
            },
            *[Symbol.iterator]() {
                let _index = 0;
                while (_index < l) {
                    yield dataSet.encodedData[_index][0];
                }
            },
        };
    }
    /**
     * Returns a getter function that returns raw/encoded data for given tupleIndex
     * at a given data index.
     * @param tupleIndex the index of the encoded channel to return a getter for.
     * @returns the getter for tupleIndex.
     */
    rawData(tupleIndex) {
        //increase the tupleIndex since internally the tuple start with the timestamp
        tupleIndex++;
        return (index) => this.encodedData[index][tupleIndex];
    }
    /**
     * Returns a getter function that returns probe/decoded data for given probeIndex
     * at a given data index.
     * @param probeIndex the index of the decoded probe to return a getter for.
     * @returns the getter for probeIndex.
     */
    data(probeIndex) {
        //    probeIndex;
        return (index) => {
            checkBounds(index, this.encodedData);
            return this._handlers[this._probeToHandler[probeIndex].handler].decodeProbe(this._probeToHandler[probeIndex].probe, this.encodedData[index], this._probeToHandler[probeIndex].tuple);
        };
    }
}
